import React, { useState, useEffect } from 'react';
import { Box, Button, Container, Typography, Grid, Card, CardContent, useTheme, Avatar } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, PackageSearch, Banknote, Users, BookOpen, ShieldCheck, ArrowRight, TrendingUp, PieChart as IconPieChart, BarChart as IconBarChart } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, PieChart as RechartsPieChart, Pie, Cell, BarChart as RechartsBarChart, Bar, Legend } from 'recharts';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.5, ease: "easeOut" }
  }
};

const features = [
  {
    icon: <ShoppingCart size={40} color="#4776E6" />,
    title: 'Smart POS & Invoicing',
    description: 'Process lightning-fast counter sales or generate professional B2B invoices in seconds. Complete with thermal print presets and one-click PDF generation.',
    color: '#4776E6'
  },
  {
    icon: <PackageSearch size={40} color="#8E54E9" />,
    title: 'Real-Time Inventory',
    description: 'Never miss a sale. Track stock levels automatically, receive intelligent low-stock alerts, and visualize your top-selling items.',
    color: '#8E54E9'
  },
  {
    icon: <Banknote size={40} color="#2ECC71" />,
    title: 'Smart Reconciliation',
    description: 'Drop your bank\'s CSV statement into our engine and watch the magic happen. Instantly match inward payments to unpaid invoices.',
    color: '#2ECC71'
  },
  {
    icon: <Users size={40} color="#FF6B35" />,
    title: 'Auto-Settlement Payroll',
    description: 'Run payroll effortlessly. Our intelligent engine automatically calculates basic pay, HRA, and auto-deducts unrecovered staff cash advances.',
    color: '#FF6B35'
  },
  {
    icon: <BookOpen size={40} color="#3498DB" />,
    title: 'Double-Entry Ledgers',
    description: 'Keep a perfect pulse on your liquidity. Every transaction automatically flows into your Cashbook and Bankbook.',
    color: '#3498DB'
  },
  {
    icon: <ShieldCheck size={40} color="#E74C3C" />,
    title: 'Enterprise Security',
    description: 'Built on AWS Cognito. Assign strict Role-Based Access Control (RBAC) to Admins, Accountants, and Cashiers.',
    color: '#E74C3C'
  }
];

const TypewriterText = ({ texts, typingSpeed = 150, deletingSpeed = 50, delay = 2000 }) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);

  useEffect(() => {
    let timeout;
    
    if (isWaiting) {
      timeout = setTimeout(() => {
        setIsWaiting(false);
        setIsDeleting(true);
      }, delay);
      return () => clearTimeout(timeout);
    }

    const currentString = texts[currentIndex];
    
    if (isDeleting) {
      if (displayText.length === 0) {
        setIsDeleting(false);
        setCurrentIndex((prev) => (prev + 1) % texts.length);
      } else {
        timeout = setTimeout(() => {
          setDisplayText(currentString.substring(0, displayText.length - 1));
        }, deletingSpeed);
      }
    } else {
      if (displayText.length === currentString.length) {
        setIsWaiting(true);
      } else {
        timeout = setTimeout(() => {
          setDisplayText(currentString.substring(0, displayText.length + 1));
        }, typingSpeed);
      }
    }

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, isWaiting, currentIndex, texts, typingSpeed, deletingSpeed, delay]);

  return (
    <span style={{ color: '#8E54E9', position: 'relative' }}>
      {displayText}
      <motion.span
        animate={{ opacity: [1, 0] }}
        transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
        style={{ position: 'absolute', right: '-12px', fontWeight: '100' }}
      >
        |
      </motion.span>
    </span>
  );
};

// Mockup Data
const lineData = [
  { name: 'Jan', sales: 12, expenses: 8 },
  { name: 'Feb', sales: 18, expenses: 10 },
  { name: 'Mar', sales: 15, expenses: 12 },
  { name: 'Apr', sales: 25, expenses: 15 },
  { name: 'May', sales: 20, expenses: 12 },
  { name: 'Jun', sales: 30, expenses: 18 },
];

const pieData = [
  { name: 'Sales', value: 400 },
  { name: 'Purchases', value: 300 },
  { name: 'Expense', value: 300 },
  { name: 'Profit', value: 200 },
];
const COLORS = ['#4776E6', '#8E54E9', '#FF6B35', '#2ECC71'];

const barData = [
  { name: 'Invoice 1', amount: 1500 },
  { name: 'Invoice 2', amount: 2300 },
  { name: 'Invoice 3', amount: 3400 },
  { name: 'Invoice 4', amount: 1200 },
  { name: 'Invoice 5', amount: 2800 },
];

const DashboardMockup = () => {
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 750,
        height: 'auto',
        minHeight: 550,
        bgcolor: '#ffffff',
        borderRadius: 4,
        boxShadow: '0px 20px 50px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(0,0,0,0.05)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}
    >
      {/* Header */}
      <Box sx={{ height: 60, display: 'flex', alignItems: 'center', px: 4, pt: 2, justifyContent: 'flex-start' }}>
        <Typography variant="h6" fontWeight="bold" sx={{ color: '#4776E6' }}>Dashboard Preview</Typography>
      </Box>

      {/* Content */}
      <Box sx={{ p: 4, pt: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        
        {/* Line Chart */}
        <Box sx={{ height: 200, display: 'flex', flexDirection: 'column' }}>
           <Typography variant="body2" fontWeight="bold" color="text.secondary" textAlign="center" mb={1}>Financial Performance</Typography>
           <Box sx={{ flexGrow: 1, width: '100%' }}>
             <ResponsiveContainer width="100%" height="100%">
                <LineChart data={lineData} margin={{ top: 10, right: 20, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                  <Legend iconType="rect" wrapperStyle={{ fontSize: 12, paddingTop: '10px' }} />
                  <Line type="monotone" dataKey="sales" name="Sales" stroke="#4776E6" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} animationDuration={2000} />
                  <Line type="monotone" dataKey="expenses" name="Expenses" stroke="#FF6B35" strokeWidth={2} dot={{ r: 4 }} animationDuration={2000} />
                </LineChart>
             </ResponsiveContainer>
           </Box>
        </Box>

        {/* Bottom Charts */}
        <Box sx={{ display: 'flex', gap: 4, height: 200 }}>
           {/* Pie Chart */}
           <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="body2" fontWeight="bold" color="text.secondary" mb={1}>Revenue Distribution</Typography>
              <Box sx={{ flexGrow: 1, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={pieData}
                      cx="30%"
                      cy="50%"
                      innerRadius={30}
                      outerRadius={50}
                      paddingAngle={2}
                      dataKey="value"
                      animationDuration={1500}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend layout="vertical" verticalAlign="middle" align="right" iconType="rect" wrapperStyle={{ fontSize: 10 }} />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </Box>
           </Box>
           
           {/* Bar Chart */}
           <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="body2" fontWeight="bold" color="text.secondary" mb={1}>Top Invoices</Typography>
              <Box sx={{ flexGrow: 1, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsBarChart data={barData} margin={{ top: 10, right: 10, left: -25, bottom: 20 }}>
                    <XAxis dataKey="name" tick={{ fontSize: 8 }} axisLine={false} tickLine={false} angle={-30} textAnchor="end" />
                    <YAxis tick={{ fontSize: 8 }} axisLine={false} tickLine={false} />
                    <Bar dataKey="amount" name="Amount (₹)" fill="#8E54E9" radius={[2, 2, 0, 0]} animationDuration={1500} />
                  </RechartsBarChart>
                </ResponsiveContainer>
              </Box>
           </Box>
        </Box>

      </Box>
    </Box>
  );
};

const Home: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box sx={{ width: '100%', overflow: 'hidden', bgcolor: '#ffffff' }}>
      {/* Hero Section */}
      <Box 
        sx={{ 
          pt: { xs: 8, md: 10 }, 
          pb: { xs: 10, md: 12 },
          position: 'relative'
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={5}>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                style={{ paddingRight: theme.spacing(2) }}
              >
                <motion.div variants={itemVariants}>
                  <Typography 
                    variant="h1" 
                    component="h1" 
                    fontWeight="800" 
                    gutterBottom
                    sx={{
                      fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                      lineHeight: 1.2,
                      color: '#1a1a1a',
                      letterSpacing: '-0.02em',
                      mb: 2,
                      minHeight: { xs: 'auto', md: '120px' }
                    }}
                  >
                    Accounting Services <br/>
                    <TypewriterText texts={['On Your Go!', 'Made Simple.', 'For India.']} />
                  </Typography>
                </motion.div>
                
                <motion.div variants={itemVariants}>
                  <Typography variant="h5" color="text.secondary" paragraph sx={{ mb: 4, fontWeight: 400, fontSize: '1.1rem', lineHeight: 1.6, maxWidth: 500 }}>
                    Simple, Intuitive Accounting Software for Indian Small Businesses. Manage invoices, inventory, and payroll effortlessly.
                  </Typography>
                </motion.div>
                
                <motion.div variants={itemVariants}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button 
                      variant="contained" 
                      size="large"
                      onClick={() => navigate('/signup')}
                      endIcon={<ArrowRight size={18} />}
                      sx={{ 
                        background: '#8E54E9',
                        color: 'white',
                        px: 4, 
                        py: 1.2,
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        borderRadius: '50px',
                        textTransform: 'none',
                        boxShadow: '0 8px 15px rgba(142, 84, 233, 0.3)',
                        '&:hover': {
                          background: '#7540cc',
                          boxShadow: '0 12px 20px rgba(142, 84, 233, 0.4)',
                          transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.3s'
                      }}
                    >
                      Get Started For Free
                    </Button>
                  </Box>
                </motion.div>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={7} sx={{ display: 'flex', justifyContent: 'center' }}>
               <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
               >
                 <Box sx={{ position: 'relative', width: '100%', maxWidth: 750 }}>
                    {/* Decorative background blob */}
                    <Box 
                      sx={{ 
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '120%',
                        height: '120%',
                        background: 'radial-gradient(circle, rgba(142,84,233,0.1) 0%, rgba(255,255,255,0) 60%)',
                        zIndex: 0
                      }} 
                    />
                    <Box sx={{ position: 'relative', zIndex: 1, width: '100%' }}>
                       <DashboardMockup />
                    </Box>
                 </Box>
               </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Grid */}
      <Box sx={{ py: { xs: 10, md: 15 }, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="xl">
          <Box textAlign="center" mb={10}>
            <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: '#1a1a1a' }}>
              Everything you need to succeed
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              Powerful tools designed specifically to help you manage and grow your business with confidence.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  style={{ height: '100%' }}
                >
                  <Card 
                    elevation={0}
                    sx={{ 
                      height: '100%',
                      borderRadius: 4,
                      border: '1px solid rgba(0,0,0,0.05)',
                      bgcolor: '#ffffff',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        transform: 'translateY(-10px)',
                        boxShadow: `0 20px 40px ${feature.color}15`,
                        borderColor: feature.color
                      }
                    }}
                  >
                    <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ 
                          width: 64, 
                          height: 64, 
                          borderRadius: '16px', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          bgcolor: `${feature.color}15`,
                          mb: 2
                        }}>
                          {feature.icon}
                        </Box>
                      </Box>
                      <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ minHeight: 40, color: '#1a1a1a' }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ flexGrow: 1, lineHeight: 1.6 }}>
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box 
        sx={{ 
          py: 12, 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #8E54E9 0%, #4776E6 100%)',
          color: 'white'
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            Ready to streamline your business?
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 6, fontWeight: 400 }}>
            Join thousands of businesses managing their operations with Akmo Books.
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            onClick={() => navigate('/signup')}
            sx={{ 
              bgcolor: 'white',
              color: '#8E54E9',
              px: 6, 
              py: 2,
              fontSize: '1.2rem',
              fontWeight: 'bold',
              borderRadius: '50px',
              '&:hover': {
                bgcolor: '#f5f5f5',
                transform: 'translateY(-2px)',
                boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
              },
              transition: 'all 0.3s'
            }}
          >
            Create Your Free Account
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;